import { BaseApi } from '@core/api';
import type { Property } from '@core/models/properties';
import type { FieldSeasonData } from '@core/models/seasons';
import type { RegionDTO } from '@state/regions/region.models';
import { RegionUtils } from '@state/regions/region.util';
import { DATE_FORMATS } from '@utils/dateUtils';
import dayjs from 'dayjs';
import {
  getRegionTreeCacheGeneration,
  invalidateRegionTreeCache,
  readRegionTreeCache,
  writeRegionTreeCache,
} from './region.cache';

export class EmptyRegionTreeError extends Error {
  constructor() {
    super('Region tree is empty: no children regions or fields');
    this.name = 'EmptyRegionTreeError';
  }
}

interface FetchRegionsOptions {
  // Ignores the IndexedDB copy, e.g. after the Fields MFE changed the property fields.
  skipCache?: boolean;
  referenceDate?: string;
}

const hasRegionFields = (region: RegionDTO): boolean =>
  region.fields.length > 0 || region.children_region.some(hasRegionFields);

export async function fetchRegions(
  property: Property,
  seasons?: FieldSeasonData[],
  {
    skipCache = false,
    referenceDate = dayjs().format(DATE_FORMATS.USA_FORMAT),
  }: FetchRegionsOptions = {}
) {
  // Keep dated map geometries separate from the undated cache used by reports.
  const cacheKey = `region-tree:${property.id}:${referenceDate}`;
  const successfulFieldEdit = window.location.href.includes('result=success');
  const isOnFieldsScreen = window.location.href.includes('/fields');
  const invalidateCache = skipCache || (successfulFieldEdit && isOnFieldsScreen);

  const invalidation = invalidateCache ? invalidateRegionTreeCache(property.id) : undefined;
  const generation = getRegionTreeCacheGeneration(property.id);
  await invalidation;
  const propertyCache = await readRegionTreeCache(property.id, cacheKey);

  const isStale =
    invalidateCache ||
    !propertyCache?.property ||
    dayjs(property.fields_updated_at).isAfter(dayjs(propertyCache.property.fields_updated_at));

  const data = isStale
    ? await BaseApi.get<RegionDTO>(
        `/properties/${property.id}/region-tree?reference_date=${referenceDate}`
      ).then((response) => response.data)
    : propertyCache.regionTreeResponse;

  if (isStale) {
    await writeRegionTreeCache(property.id, cacheKey, generation, {
      ...propertyCache,
      regionTreeResponse: { ...data },
      property,
    });
  }

  if (!hasRegionFields(data)) {
    throw new EmptyRegionTreeError();
  }

  const parsedData = RegionUtils.parseRegionTreeToMap(data, seasons);

  return parsedData;
}
